"use client";

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArrowLeft, Home, LogIn, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader } from '@/components/ui/glass-card';
import { safeAssignLocation } from '@/lib/security/safeRedirect';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { buildBrandStyle } from '@/lib/ui/brand';
type BackNavigation = Pick<History, 'back' | 'length'>;
export function goBackOrHome(historyLike: BackNavigation, assignLocation = safeAssignLocation) {
  if (historyLike.length > 1) {
    historyLike.back();
    return;
  }
  assignLocation('/', {
    sameOriginOnly: true
  }, () => {
    if (typeof window !== 'undefined') window.location.href = '/';
  });
}
function AnimatedBackground() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute -top-1/2 -left-1/4 h-[800px] w-[800px] rounded-full bg-gradient-to-br from-cyan-500/20 via-teal-500/15 to-transparent blur-[120px] animate-pulse" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="fixed inset-0 -z-10 overflow-hidden">{t0}<div className="absolute -bottom-1/2 -right-1/4 h-[600px] w-[600px] rounded-full bg-gradient-to-br from-orange-500/20 via-amber-500/10 to-transparent blur-[100px] animate-pulse" style={{
        animationDelay: "1s"
      }} /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export default function NotFound() {
  const $ = _c(10);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = buildBrandStyle("#0f766e", "#f97316");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const brandStyle = t0;
  const handleGoBack = _temp;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AnimatedBackground />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="fixed top-4 right-4 z-50"><ThemeToggle /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mx-auto inline-flex items-center rounded-full border border-border/40 bg-background/60 px-3 py-1 text-sm font-medium text-muted-foreground backdrop-blur">Error 404: page has left the chat</div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10"><Search className="h-7 w-7 text-primary" aria-hidden="true" /></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <GlassCardHeader className="space-y-4 text-center">{t3}{t4}<div className="space-y-2"><h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">404 — Well, this is awkward.</h1><GlassCardDescription className="mx-auto max-w-xl text-base leading-relaxed">The page you were looking for has gone for a tea break, hidden behind a typo, or wandered into another dimension.</GlassCardDescription></div></GlassCardHeader>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="grid gap-3 text-sm text-muted-foreground sm:grid-cols-3"><div className="rounded-lg border border-border/40 bg-background/40 p-3">Check the address for any rebellious slashes.</div><div className="rounded-lg border border-border/40 bg-background/40 p-3">Head back home and pretend this never happened.</div><div className="rounded-lg border border-border/40 bg-background/40 p-3">If you needed sign-in, use the link from your provider or team.</div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Button asChild={true} size="lg" className="min-w-40"><Link href="/"><Home className="h-4 w-4" aria-hidden="true" />Take me home</Link></Button>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Button variant="outline" size="lg" className="min-w-40" onClick={handleGoBack}><ArrowLeft className="h-4 w-4" aria-hidden="true" />Go back</Button>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="min-h-screen" style={brandStyle}>{t1}{t2}<main className="min-h-screen flex items-center justify-center px-6 py-12"><div className="w-full max-w-2xl"><GlassCard variant="strong" className="w-full">{t5}<GlassCardContent className="space-y-6 text-center">{t6}<div className="flex flex-col justify-center gap-3 sm:flex-row">{t7}{t8}<Button asChild={true} variant="outline" size="lg" className="min-w-40"><Link href="/admin/login"><LogIn className="h-4 w-4" aria-hidden="true" />Admin login</Link></Button></div></GlassCardContent></GlassCard></div></main></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
}
function _temp() {
  return goBackOrHome(window.history);
}